// react library imports
import { lazy, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useSelector, useDispatch} from 'react-redux';

// import from redux store
import { getActiveClass } from 'Redux/class/class';
import { addAcademicPlan } from 'Redux/class/academicPlan';

// import from apis
import { useGetAcademicPlanQuery } from 'api/class/academicPlan';
import TransactionReport from 'Pages/Private/Finances/AddTransaction/TransactionReport';

// Layout imports
const DashboardLayout = lazy(() => import('Layout/Dashboard'))

// import from home section
const Home = lazy(() => import('Pages/Private/Home/Home'))

// import from team section
const Team = lazy(() => import('Pages/Private/Team/Team'))
const Teacher = lazy(() => import('Pages/Private/Team/Teacher/Teacher'))
const Admin = lazy(() => import('Pages/Private/Team/Admin/Admin'))
const HelpingStaff = lazy(() => import('Pages/Private/Team/HelpingStaff/HelpingStaff'))
const LeaderBoard = lazy(() => import('Pages/Private/Team/LeaderBoard/LeaderBoard'))

// import from classes section
const Classes = lazy(() => import('Pages/Private/Classes/Classes'))
const UploadInBulkKid = lazy(() => import('Pages/Private/Classes/Component/UploadInBulkKid/UploadInBulkKid'))
const KidVerification = lazy(() => import('Pages/Private/Classes/Component/KidVerification/KidVerification'))
const BulkUploadTerm = lazy(() => import('Pages/Private/Classes/Component/BulkUploadTerm/BulkUploadTerm'))
const KidInformation = lazy(() => import('Pages/Private/Classes/Component/KidInformation/KidInformation'))

// import from inventory section
const Inventory = lazy(() => import('Pages/Private/Inventory/Inventory'))
const AddInventory = lazy(() => import('Pages/Private/Inventory/AddInventory/AddInventory'))
const Allocation = lazy(() => import('Pages/Private/Inventory/Allocation/Allocation'))
const InventoryOverview = lazy(() => import('Pages/Private/Inventory/InventoryOverview/InventoryOverview'))

// import from finances section
const Finances = lazy(() => import('Pages/Private/Finances/Finances'))
const AddTransaction = lazy(() => import('Pages/Private/Finances/AddTransaction/AddTransaction'))
const Analysis = lazy(() => import('Pages/Private/Finances/Analysis/Analysis'))
const FeesOverview = lazy(() => import('Pages/Private/Finances/FeesOverview/FeesOverview'))
const DownloadReport = lazy(() => import('Pages/Private/Finances/FeesOverview/Component/DownloadReport/DownloadReport'))

// import from security section
const Security = lazy(() => import('Pages/Private/Security/Security'))
const CameraFeeds = lazy(() => import('Pages/Private/Security/Component/CameraFeeds/CameraFeeds'))
const OutSideProfile = lazy(() => import('Pages/Private/Security/Component/OutSideProfile/OutSideProfile'))

// import from marketing section
const Marketing = lazy(() => import('Pages/Private/Marketing/Marketing'))

// import from dashboard topbar section
const Event = lazy(() => import('Pages/Private/Event/Event'))
const Broadcast = lazy(() => import('Pages/Private/Broadcast/Broadcast'))
const ChatPage = lazy(() => import('Pages/Private/Chat/Chat'))
const Setting = lazy(() => import('Pages/Private/Setting/Setting'))

// import from sidebar footer section
const MyPlan = lazy(() => import('Pages/Private/MyPlan/MyPlan'))
const MyProfile = lazy(() => import('Pages/Private/MyProfile/MyProfile'))
const AccountAndPayments = lazy(() => import('Pages/Private/AccountAndPayments/AccountAndPayments'))
const LeaveManagement = lazy(() => import('Pages/Private/LeaveManagement/LeaveManagement'))
const Pro = lazy(() => import('Pages/Private/Pro/Pro'))

const ClassLayout = () => {
  // fetch data and define dispatch action from redux store
  const dispatch = useDispatch()
  const activeClass = useSelector(getActiveClass)

  // define params for elg information
  const elgParam = {
    preschool: activeClass?.preschool,
    program: activeClass?.program
  }
  const { data: academicPlan, isSuccess: success } = useGetAcademicPlanQuery(elgParam)
  // post data on the redux when the data is fetch from apis
  useEffect(() => {
    if (success) {
      dispatch(addAcademicPlan(academicPlan?.academic_year_plan))
    }
  }, [success, dispatch, academicPlan])

  return (
    <Outlet />
  )
}

export const dashboardRoutes = [
  {
    path: "dashboard/",
    element: <DashboardLayout />,
    children: [
      { index: true, element: <Home /> },
      {
        path: "team/",
        element: <Team />,
        children: [
          { path: 'teacher', element: <Teacher /> },
          { path: 'admin', element: <Admin /> },
          { path: 'helping-staff', element: <HelpingStaff /> },
          { path: 'leader-board', element: <LeaderBoard /> },
        ],
      },
      {
        path: "classes",
        element: <ClassLayout />,
        children: [
          { path: "add-multiple-students", element: <UploadInBulkKid /> },
          { path: "kid-verification", element: <KidVerification /> },
          { path: "create-term", element: <BulkUploadTerm /> },
          { path: "kid", element: <KidInformation /> },
          { path: ":id", element: <Classes /> },
        ],
      },
      {
        path: "inventory/",
        element: <Inventory />,
        children: [
          { path: "inventory-overview", element: <InventoryOverview /> },
          { path: "inventory", element: <Allocation /> },
          { path: "add-vendors", element: <AddInventory /> },
        ],
      },
      {
        path: "finances",
        element: <Finances />,
        children: [
          { path: "fees-overview", element: <FeesOverview /> },
          { path: "analysis", element: <Analysis /> },
          { path: "add-transaction", element: <AddTransaction /> },
          { path: "download-report", element: <DownloadReport /> },
          { path: "transaction", element: <TransactionReport /> },
        ],
      },
      {
        path: "security",
        element: <Security />,
        children: [
          { path: "camera-feeds", element: <CameraFeeds /> },
          { path: "outsiders", element: <OutSideProfile /> },
        ],
      },
      { path: "marketing", element: <Marketing /> },
      { path: "event", element: <Event /> },
      { path: "broadcast", element: <Broadcast /> },
      { path: "chat", element: <ChatPage /> },
      { path: "leave-management", element: <LeaveManagement /> },
      { path: "leave-management/:id", element: <LeaveManagement /> },
      // { path: "notification", element: <Notification /> },
      { path: "setting", element: <Setting /> },
      { path: "plan", element: <MyPlan /> },
      { path: "Pro", element: <Pro /> },
      { path: "profile", element: <MyProfile /> },
      { path: "account-and-payments", element: <AccountAndPayments /> },
    ],
  },
];
